import React from 'react';
import { Sparkles } from 'lucide-react';
import {
  getMediaUrl,
  getConventionalFilename,
  logger,
} from '@musedrops/shared';
import { AudioFileField } from '../../../shared/components/forms/AudioFileField';
import { supabase } from '../../../services/supabase';

interface StoryAnnounceSectionProps {
  storySlug: string;
  storyId?: string;
  lang: string;
  announceUpdatedAt?: string | null;
  onAnnounceDeleted?: () => void;
  // eslint-disable-next-line no-unused-vars
  onAnnounceUpdatedAtChanged?: (timestamp: string) => void;
  // Issue #851: regeneration-wave preflight (clear downstream + reset phase).
  // Returning false cancels the regenerate.
  onBeforeRegenerate?: () => Promise<boolean>;
}

/**
 * Story Announcement Section Component
 *
 * Wrapper around AudioFileField for story announcement audio (language-specific).
 * Supports both upload and generate via gen-story-announce Edge Function.
 *
 * Issue #631: Story Announce Audio
 */
export const StoryAnnounceSection: React.FC<StoryAnnounceSectionProps> = ({
  storySlug,
  storyId,
  lang,
  announceUpdatedAt,
  onAnnounceDeleted,
  onAnnounceUpdatedAtChanged,
  onBeforeRegenerate,
}) => {
  const [isRegenerating, setIsRegenerating] = React.useState(false);
  const [activeJobId, setActiveJobId] = React.useState<number | null>(null);

  // Use conventional filename with language placeholder resolved
  const announceFile = React.useMemo(() => {
    return getConventionalFilename('story', 'storyAnnounce', lang); // e.g., 'announce_en.mp3'
  }, [lang]);

  // Construct audio URL with timestamp for cache-busting
  const audioUrl = React.useMemo(() => {
    if (storySlug && announceUpdatedAt) {
      return `${getMediaUrl('stories', storySlug, announceFile)}?t=${new Date(announceUpdatedAt).getTime()}`;
    }
    if (storySlug) {
      return getMediaUrl('stories', storySlug, announceFile);
    }
    return undefined;
  }, [storySlug, announceUpdatedAt, announceFile]);

  // Subscribe to job status updates
  React.useEffect(() => {
    if (!activeJobId) return undefined;

    logger.info('Subscribing to job status updates', { jobId: activeJobId });

    const subscription = supabase
      .channel(`job-${activeJobId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'process_queue',
          filter: `id=eq.${activeJobId}`,
        },
        payload => {
          const { status } = payload.new;
          logger.info('Job status update received', {
            jobId: activeJobId,
            status,
          });

          if (status === 'completed') {
            logger.info('Job completed successfully', { jobId: activeJobId });
            setIsRegenerating(false);
            setActiveJobId(null);
          } else if (status === 'failed') {
            logger.error('Job failed', {
              jobId: activeJobId,
              error: payload.new.error,
            });
            setIsRegenerating(false);
            setActiveJobId(null);
          }
        }
      )
      .subscribe();

    return () => {
      logger.info('Unsubscribing from job status updates', {
        jobId: activeJobId,
      });
      subscription.unsubscribe();
    };
  }, [activeJobId]);

  // Handle generate button click
  const handleGenerate = async () => {
    if (!storyId) {
      throw new Error('Story ID is required for announcement generation');
    }

    // Issue #851: regeneration wave — announce is the first translation step,
    // so this clears/regenerates the whole downstream chain on confirm
    if (onBeforeRegenerate && !(await onBeforeRegenerate())) {
      return;
    }

    setIsRegenerating(true);

    try {
      // Call gen-story-announce Edge Function
      const { data, error } = await supabase.functions.invoke(
        'gen-story-announce',
        {
          body: {
            storyId,
            lang,
            trigger: 'manual', // UI-initiated jobs get priority
          },
        }
      );

      if (error) {
        setIsRegenerating(false);
        throw new Error(`Failed to generate announcement: ${error.message}`);
      }

      if (!data?.success) {
        setIsRegenerating(false);
        throw new Error(data?.error || 'Announcement generation failed');
      }

      // Job queued successfully - subscribe to updates
      logger.info('Announcement generation queued:', data.result);
      setActiveJobId(data.result.jobId);
      // Spinner will be dismissed by subscription when job completes
    } catch (err) {
      setIsRegenerating(false);
      throw err;
    }
  };

  return (
    <AudioFileField
      label="Announcement"
      value={announceUpdatedAt ? announceFile : null} // Show filename if file exists (timestamp present)
      onUpload={() => {
        // Upload handled by AudioFileField, filename is conventional (announce_{lang}.mp3)
        // No-op handler - just enables upload button. Timestamp update handled by onTimestampChanged.
      }}
      onRegenerate={handleGenerate}
      isRegenerating={isRegenerating}
      RegenerateIcon={Sparkles}
      regenerateTitle="Generate story announcement with AI"
      onDelete={onAnnounceDeleted}
      onTimestampChanged={onAnnounceUpdatedAtChanged}
      audioUrl={audioUrl}
      storageFolder="stories"
      slug={storySlug}
      entityId={storySlug}
      tableName="story_translations"
      fileType="storyAnnounce"
      lang={lang}
      accept="audio/*"
      showGenerate
      showUpload={false}
    />
  );
};
